import { useState } from "react";
import Seo from "../components/shared/Seo";
import NoticeBanner from "../components/ui/NoticeBanner";
import EmptyState from "../components/ui/EmptyState";
import InputField from "../components/ui/InputField";
import LoadingState from "../components/ui/LoadingState";
import SectionHeading from "../components/ui/SectionHeading";
import PostCard from "../components/shared/PostCard";
import useContentResource from "../hooks/useContentResource";
import { createCollectionPageSchema } from "../seo/schema";
import { buildCanonicalUrl } from "../seo/siteConfig";
import { getBlogPosts } from "../services/content/blog.service";
import { getCategories } from "../services/content/taxonomy.service";

function BlogPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const postsState = useContentResource(
    ({ signal }) => getBlogPosts({ signal, limit: 24 }),
    []
  );
  const categoriesState = useContentResource(
    ({ signal }) => getCategories({ signal }),
    []
  );
  const posts = postsState.data || [];
  const categoryOptions = [
    "All",
    ...new Set((categoriesState.data || []).map((item) => item.name)),
  ];

  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || post.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const featuredPost = posts.find((post) => post.featured) || posts[0];

  return (
    <div className="page-stack">
      <Seo
        title="Blog"
        description="React mimarisi, premium UI, motion ve headless CMS konularinda yazilarin yer aldigi blog alani."
        path="/blog"
        schema={[
          createCollectionPageSchema({
            title: "Blog",
            description:
              "React mimarisi, premium arayuzler ve headless CMS odakli blog yazilari.",
            url: buildCanonicalUrl("/blog"),
            itemUrls: posts.map((item) =>
              buildCanonicalUrl(`/blog/${item.slug}`)
            ),
          }),
        ]}
        keywords={["blog", "React blog", "frontend yazilari"]}
      />
      {postsState.usedFallback ? (
        <NoticeBanner>
          Blog icerikleri su anda local fallback kaynagindan geliyor. WordPress
          baglantisi hazir oldugunda ayni sayfa API verisiyle calisacak.
        </NoticeBanner>
      ) : null}
      <section className="glass-panel page-hero">
        <SectionHeading
          eyebrow="Blog"
          title="Okunabilirlik ve kesif odakli bir icerik deneyimi"
          description="Listeleme, kategori secimi, arama ve detay sayfasina acilan kart yapisi ile blog alani headless CMS entegrasyonuna hazir."
        />
      </section>

      {featuredPost ? (
        <section className="content-section">
          <article className="glass-panel featured-story">
            <div>
              <p className="eyebrow">Featured Story</p>
              <h2>{featuredPost.title}</h2>
              <p>{featuredPost.excerpt}</p>
              <div className="meta-row">
                <span className="meta-text">{featuredPost.category}</span>
                <span className="meta-text">{featuredPost.readingTime}</span>
              </div>
            </div>
            <div className="featured-story-art">
              {featuredPost.coverImage ? (
                <img
                  className="card-media-image"
                  src={featuredPost.coverImage}
                  alt={featuredPost.cover || featuredPost.title}
                  loading="lazy"
                />
              ) : (
                featuredPost.cover
              )}
            </div>
          </article>
        </section>
      ) : null}

      <section className="content-section">
        <div className="glass-panel toolbar-panel">
          <InputField
            label="Yazi ara"
            placeholder="Baslik ya da ozet ara"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <div className="filter-group">
            <p className="filter-label">Kategori</p>
            <div className="tag-row">
              {categoryOptions.map((item) => (
                <button
                  key={item}
                  className={`tag-pill filter-pill${
                    selectedCategory === item ? " filter-pill-active" : ""
                  }`}
                  type="button"
                  onClick={() => setSelectedCategory(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="content-section">
        {postsState.loading ? (
          <LoadingState title="Blog yazilari yukleniyor..." />
        ) : filteredPosts.length ? (
          <div className="card-grid card-grid-three">
            {filteredPosts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Yazi bulunamadi"
            description="Farkli bir arama kelimesi ya da kategori ile tekrar deneyin."
          />
        )}
      </section>
    </div>
  );
}

export default BlogPage;
